import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/abbottLogo.png";
import orevanEventsLogo from "../../assets/orevanEventsLogo.png";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./Navbar.css";
import { usePermission } from "../../hooks/usePermission";
import {
  ViewActivityTypesPermission,
  ViewTeamOverview,
  ViewHCPMasterDataPermission,
  ViewSpendTypesPermission,
  ViewEventTypesPermission,
  ViewDivisionsPermission,
  ViewTeamsPermission,
  ViewOrganizationsPermission,
  ViewRelationshipsPermission,
  ViewExternalSourcesPermission,
  ViewRolesPermission,
  ViewEventsPermission,
  CreateUsersPermission,
  ViewUsersPermission,
  ViewCountriesPermission,
  ViewCitiesPermission,
  ViewBrandsPermission
} from "../../constants/permissions";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const location = useLocation();

  const { data: canViewUsers } = usePermission({
    permissionName: ViewUsersPermission,
  });

  const { data: canCreateUser } = usePermission({
    permissionName: CreateUsersPermission,
  });
  const { data: canViewEvents } = usePermission({
    permissionName: ViewEventsPermission,
  });

  const { data: canViewOwnEventsAndEventsTeamScope } = usePermission({
    permissionName: ViewTeamOverview,
  });

  const { data: canViewHCPMasterData } = usePermission({
    permissionName: ViewHCPMasterDataPermission,
  });

  const { data: canViewSpendTypes } = usePermission({
    permissionName: ViewSpendTypesPermission,
  });

  const { data: canViewActivityTypes } = usePermission({
    permissionName: ViewActivityTypesPermission,
  });

  const { data: canViewEventTypes } = usePermission({
    permissionName: ViewEventTypesPermission,
  });

  const { data: canViewDivisions } = usePermission({
    permissionName: ViewDivisionsPermission,
  });

  const { data: canViewTeams } = usePermission({
    permissionName: ViewTeamsPermission,
  });

  const { data: canViewOrganizations } = usePermission({
    permissionName: ViewOrganizationsPermission,
  });

  const { data: canViewRelationships } = usePermission({
    permissionName: ViewRelationshipsPermission,
  });

  const { data: canViewExternalSources } = usePermission({
    permissionName: ViewExternalSourcesPermission,
  });

  const { data: canViewRoles } = usePermission({
    permissionName: ViewRolesPermission,
  });

  const { data: canViewCountries } = usePermission({
    permissionName: ViewCountriesPermission,
  });

  const { data: canViewCities } = usePermission({
    permissionName: ViewCitiesPermission,
  });
  const { data: canViewBrands } = usePermission({
    permissionName: ViewBrandsPermission,
  });


  const hasSettingsAccess =
    canViewSpendTypes ||
    canViewActivityTypes ||
    canViewEventTypes ||
    canViewDivisions ||
    canViewTeams ||
    canViewOrganizations ||
    canViewHCPMasterData ||
    canViewRelationships ||
    canViewExternalSources ||
    canViewCountries ||
    canViewCities ||
    canViewBrands;

  const navItems = [
    { path: "/", label: "Dashboard", icon: "fas fa-home", permission: true },
    {
      path: "/events",
      label: "Events",
      icon: "fas fa-calendar-alt",
      permission: canViewEvents,
    },
    {
      path: "/teams-overview",
      label: "Teams",
      icon: "fa-solid fa-users",
      permission: canViewOwnEventsAndEventsTeamScope,
    },
    {
      path: "/users",
      label: "Users",
      icon: "fa-solid fa-users-viewfinder",
      permission: canViewUsers,
    },
    {
      path: "/users/create",
      label: "Create Users",
      icon: "fa-solid fa-people-group",
      permission: canCreateUser,
    },
  ];

  const settingsItems = [
    {
      path: "/countries",
      label: "Countries",
      icon: "fa-solid fa-globe",
      permission: canViewCountries,
    },
    {
      path: "/cities",
      label: "Cities",
      icon: "fa-solid fa-city",
      permission: canViewCities,
    },
    {
      path: "/spend-types",
      label: "Spend Types",
      icon: "fa-solid fa-money-bill",
      permission: canViewSpendTypes,
    },
    {
      path: "/activity-types",
      label: "Activity Types",
      icon: "fa-solid fa-list-check",
      permission: canViewActivityTypes,
    },
    {
      path: "/event-types",
      label: "Event Types",
      icon: "fa-solid fa-calendar",
      permission: canViewEventTypes,
    },
    {
      path: "/divisions",
      label: "Divisions",
      icon: "fa-solid fa-building",
      permission: canViewDivisions,
    },
    {
      path: "/teams",
      label: "Teams",
      icon: "fa-solid fa-users",
      permission: canViewTeams,
    },
    {
      path: "/brands",
      label: "Brands",
      icon: "fa-solid fa-layer-group",
      permission: canViewBrands,
    },
    {
      path: "/hcp-master-data",
      label: "HCP Master Data",
      icon: "fa-solid fa-user-pen",
      permission: canViewHCPMasterData,
    },
    {
      path: "/organizations",
      label: "Organizations",
      icon: "fa-solid fa-building-user",
      permission: canViewOrganizations,
    },
    {
      path: "/relationships",
      label: "Relationships",
      icon: "fa-solid fa-handshake",
      permission: canViewRelationships,
    },
    {
      path: "/external-sources",
      label: "External Sources",
      icon: "fa-solid fa-external-link",
      permission: canViewExternalSources,
    },
    {
      path: "/roles",
      label: "Roles",
      icon: "fa-solid fa-user-shield",
      permission: canViewRoles,
    },

  ];

  return (
    <>
      {/* Toggle Button (Mobile) */}
      <div className="lg:hidden sm:flex md:flex justify-start items-start p-5 bg-white shadow h-fit w-fit relative">
        <button onClick={() => setIsOpen(!isOpen)} className="text-2xl">
          <i className={`fa-solid absolute z-50 top-2 right-2.5  ${isOpen ? "fa-xmark" : "fa-bars"}`}></i>
        </button>
      </div>

      {/* Sidebar / Drawer */}
      <div
        className={`MainNav shadow-lg relative transition-transform transform ${isOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 lg:block`}
      >
        <div className="flex justify-center my-10">
          <img className="w-[65%] md:w-3/4" src={logo} alt="Logo" />
        </div>

        <ul className="list-none p-0 m-0">
          {navItems.map(
            (item) =>
              item.permission && (
                <li
                  key={item.path}
                  className={`px-4 py-3 hover:bg-primary transition-all text-lg ${location.pathname === item.path
                    ? "bg-blue-50 font-bold"
                    : ""
                    }`}
                >
                  <Link to={item.path} className="flex items-center gap-2 ml-2">
                    <span className="w-8 inline-block">
                      <i className={item.icon}></i>
                    </span>
                    {item.label}
                  </Link>
                </li>
              )
          )}

          {hasSettingsAccess && (
            <li className="px-4 py-3 hover:bg-primary transition-all text-lg">
              <div
                className="flex items-center gap-2 ml-2 cursor-pointer"
                onClick={() => setSettingsOpen(!settingsOpen)}
              >
                <span className="w-8 inline-block">
                  <i className="fa-solid fa-gear"></i>
                </span>
                Settings
                <i
                  className={`fa-solid fa-chevron-${settingsOpen ? "up" : "down"
                    } ml-auto text-sm`}
                ></i>
              </div>
              {settingsOpen && (
                <ul className="list-none pl-4 mt-2">
                  {settingsItems.map(
                    (item) =>
                      item.permission && (
                        <li
                          key={item.path}
                          className={`p-2 hover:bg-primary transition-all text-md ${location.pathname === item.path
                            ? "bg-blue-50 font-bold"
                            : ""
                            }`}
                        >
                          <Link
                            to={item.path}
                            className="flex items-center gap-2"
                          >
                            <span className="w-8 inline-block">
                              <i className={item.icon}></i>
                            </span>
                            {item.label}
                          </Link>
                        </li>
                      )
                  )}
                </ul>
              )}
            </li>
          )}
        </ul>
        <div className={`logo ${settingsOpen ? "mt-4" : "absolute bottom-5 left-0 right-0"}`}>
          <img src={orevanEventsLogo} alt="Logo" width={200} height={200} className="mx-auto mt-4" />
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {isOpen && (
        <div
          className="fixed inset-0  lg:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </>
  );
}
